import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-data-grid-premium/themeAugmentation'
import type {} from '@mui/x-date-pickers-pro/themeAugmentation'
import { light } from 'themes'
import BorderRadius from '../figma/tokens/BorderRadius'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import Shadows from '../figma/tokens/Shadows'
import Spacings from '../figma/tokens/Spacings'

const DefaultMaterialTheme = () =>
  createTheme(
    {
      components: {
        MuiDataGrid: {
          styleOverrides: {
            root: {
              fontSize: 14,
            },
            // Cells are plain `display: block` by default, so single-line text sits per normal
            // text flow (visually top-of-row) rather than centered in the (often taller) row.
            cell: {
              display: 'flex',
              alignItems: 'center',
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              fontSize: 16,
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              fontSize: 16,
            },
          },
        },
        MuiList: {
          styleOverrides: {
            root: {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
        MuiCollapse: {
          styleOverrides: {
            root: {
              '& .SnackbarContent-root.SnackbarContent-root': {
                fontFamily: 'Inter',
                fontWeight: 600,
                '.MuiButton-root .MuiSvgIcon-root': {
                  color: ColorsDeprecated.baseStone,
                },
              },
              '& .SnackbarContent-root.SnackbarItem-variantInfo': {
                backgroundColor: 'white',
                color: 'black',
                '& .MuiSvgIcon-root': {
                  color: ColorsDeprecated.textLink,
                },
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              '.MuiInputBase-adornedStart': {
                padding: 0,
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: `1px solid ${ColorsDeprecated.baseStone}`,
                borderRadius: 0,
              },
              fontSize: 16,
            },
          },
        },
        MuiButtonBase: { defaultProps: { disableRipple: true } },
        MuiButton: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiInput: {
          styleOverrides: {
            sizeSmall: {
              fontSize: 10,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            sizeSmall: {
              padding: 0,
              height: 32,
              fontSize: 14,
            },
            root: {
              // height: INPUT_HEIGHT,
              borderRadius: BorderRadius.soft,
              borderColor: ColorsDeprecated.baseStone,
              '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
                borderColor: ColorsDeprecated.baseStone,
              },
            },
            input: {
              borderRadius: BorderRadius.soft,
              padding: '12.5px',
            },
            multiline: {
              borderRadius: 'initial',
            },
            adornedEnd: {
              paddingRight: 0,
            },
          },
        },
        // Mirrors MuiOutlinedInput above: the date/date-range pickers render through their own
        // PickersOutlinedInput component (not MuiOutlinedInput) since accessible field DOM
        // structure became mandatory in MUI X v9, so it needs the same visual treatment applied
        // separately to still match every other outlined field in the app.
        MuiPickersOutlinedInput: {
          styleOverrides: {
            root: {
              // MuiOutlinedInput's <input> gets the browser default 1rem regardless of the
              // theme's typography.fontSize (18); PickersInputBase has no such reset and
              // inherits typography.fontSize directly, rendering visibly larger otherwise.
              fontSize: '1rem',
              borderRadius: BorderRadius.soft,
              borderColor: ColorsDeprecated.baseStone,
              '&:hover:not($disabled):not($focused):not($error) $notchedOutline': {
                borderColor: ColorsDeprecated.baseStone,
              },
            },
            // The accessible field structure renders its visible content through
            // sectionsContainer, not the (now hidden, height:1px) <input> element --
            // MuiOutlinedInput's `input: { padding: '12.5px' }` has no equivalent effect here.
            sectionsContainer: {
              padding: '12.5px 0',
            },
            adornedEnd: {
              paddingRight: 0,
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            sizeSmall: {
              fontSize: 14,
            },
            root: {
              fontSize: 16,
            },
            outlined: {
              top: '-3px',
              '&$focused': {
                top: 0,
              },
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              color: ColorsDeprecated.baseGrey,
              backgroundColor: ColorsDeprecated.baseWhite,
              borderRadius: BorderRadius.soft,
              boxShadow: Shadows.baseHeavy,
              padding: Spacings.small,
              fontFamily: 'Inter',
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              height: 3,
              minWidth: 120,
            },
            list: {
              borderBottom: `1px solid ${ColorsDeprecated.baseStone}`,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              width: 120,
              '@media (min-width: 0px)': {
                minWidth: 120,
              },
              textTransform: 'none',
              '&:hover': {
                color: ColorsDeprecated.textLinkHover,
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            colorSecondary: {
              color: ColorsDeprecated.baseGreen,
              '&$checked': {
                color: ColorsDeprecated.baseGreen,
              },
              '&$hover': {
                color: ColorsDeprecated.baseGreen,
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              '&.MuiInputAdornment-positionEnd': {},
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            multiple: {
              padding: 8,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              fontSize: 16,
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: 16,
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            paper: {
              fontSize: 16,
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            switchBase: {
              margin: '2px !important',
            },
          },
        },
      },
    },
    light,
  )

export default DefaultMaterialTheme()
